// IntakePublicPage — public enrollment form at /intake/:token. NO auth.
//
// Shown after a client accepts a plan on the quote landing. Collects the
// full client details (PHI — DOB, SSN, address, health) and on submit the
// backend creates the Client (encrypted) + Policy and converts the quote.
// Everything sensitive is encrypted server-side; this page only collects.
import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Check, ShieldCheck, AlertCircle, CheckCircle2 } from "lucide-react";
import { getPublicIntake, submitPublicIntake } from "../api.js";

const LANG = (typeof navigator !== "undefined" && (navigator.language || "").toLowerCase().startsWith("es")) ? "es" : "en";
const t = (es, en) => (LANG === "es" ? es : en);

const MARITAL = [
  ["", t("Selecciona…", "Select…")],
  ["single", t("Soltero/a", "Single")],
  ["married", t("Casado/a", "Married")],
  ["divorced", t("Divorciado/a", "Divorced")],
  ["widowed", t("Viudo/a", "Widowed")],
  ["domestic_partner", t("Unión libre", "Domestic partner")],
];

export function IntakePublicPage({ token }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [linkGone, setLinkGone] = useState(false); // 410: existió y ya no vale
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", dateOfBirth: "",
    ssn: "", address: "", city: "", state: "", zipCode: "",
    maritalStatus: "", healthNotes: "", consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    let off = false;
    setLoadError("");
    getPublicIntake(token)
      .then((d) => {
        if (off) return;
        setData(d);
        // Prefill name + email from the quote.
        const parts = String(d.recipientName || "").trim().split(/\s+/);
        setForm((f) => ({
          ...f,
          firstName: parts.shift() || "",
          lastName: parts.join(" ") || "",
          email: d.recipientEmail || "",
        }));
      })
      .catch((err) => { if (off) return; setLinkGone(!!err.linkGone); setLoadError(err.message || String(err)); });
    return () => { off = true; };
  }, [token]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  async function handleSubmit() {
    setSubmitError("");
    const required = [
      ["firstName", t("nombre", "first name")],
      ["lastName", t("apellido", "last name")],
      ["email", t("correo", "email")],
      ["phone", t("teléfono", "phone")],
      ["dateOfBirth", t("fecha de nacimiento", "date of birth")],
      ["address", t("dirección", "address")],
      ["city", t("ciudad", "city")],
      ["state", t("estado", "state")],
      ["zipCode", t("código postal", "ZIP code")],
    ];
    const missing = required.filter(([k]) => !String(form[k] || "").trim());
    if (missing.length) {
      setSubmitError(t(`Falta: ${missing.map((m) => m[1]).join(", ")}.`, `Missing: ${missing.map((m) => m[1]).join(", ")}.`));
      return;
    }
    if (!form.consent) {
      setSubmitError(t("Debes autorizar el uso de tu información para continuar.", "You must authorize the use of your information to continue."));
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitPublicIntake(token, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        dateOfBirth: form.dateOfBirth,
        ssn: form.ssn.trim() || undefined,
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zipCode: form.zipCode.trim(),
        maritalStatus: form.maritalStatus || undefined,
        healthNotes: form.healthNotes.trim() || undefined,
        consent: true,
      });
      setDone(res || {});
    } catch (err) {
      const msg = String(err.message || err);
      if (/already_submitted/.test(msg)) {
        setDone({ already: true });
      } else if (/invalid_dob/.test(msg)) {
        setSubmitError(t("La fecha de nacimiento no es válida.", "The date of birth isn't valid."));
      } else {
        setSubmitError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── styles (mirror BookingPublicPage) ──
  const page = { minHeight: "100vh", background: "linear-gradient(160deg,#eef6ff,#f8fbff)", padding: "24px 14px", boxSizing: "border-box", fontFamily: "system-ui,Arial,sans-serif", color: "#1a2433" };
  const card = { maxWidth: 580, margin: "0 auto", background: "#fff", borderRadius: 18, boxShadow: "0 10px 40px rgba(10,85,140,.12)", overflow: "hidden" };
  const pad = { padding: "22px 24px" };
  const labelSt = { display: "block", fontSize: 12.5, fontWeight: 600, color: "#475569", marginBottom: 5 };
  const inputSt = { width: "100%", padding: "10px 12px", borderRadius: 9, border: "1px solid #cbd5e1", fontSize: 15, boxSizing: "border-box" };

  // Vencido y roto no son lo mismo: el primero se resuelve pidiendo otro
  // enlace, el segundo no. Ver el comentario gemelo en ApplicationPublicPage.
  if (loadError) {
    return (
      <div style={page}>
        <div style={{ ...card, ...pad, textAlign: "center" }}>
          <AlertCircle size={34} style={{ color: linkGone ? "#d97706" : "#e11d48", marginBottom: 8 }} />
          <h2 style={{ margin: "6px 0" }}>
            {linkGone ? t("Este enlace ya venció", "This link has expired") : t("Enlace no disponible", "Link unavailable")}
          </h2>
          <p style={{ color: "#64748b", fontSize: 14 }}>
            {linkGone
              ? t("Por tu seguridad, el formulario deja de abrirse a las 72 horas. Pídele a tu agente que te reenvíe el enlace: te llega uno nuevo al instante y no tendrás que empezar de cero.",
                  "For your security, the form stops opening after 72 hours. Ask your agent to resend the link — a new one arrives right away and you won't have to start over.")
              : t("Este enlace de inscripción no existe o ya expiró. Verifica con tu agente.",
                  "This enrollment link doesn't exist or has expired. Check with your agent.")}
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={page}>
        <div style={{ ...card, ...pad, textAlign: "center", color: "#64748b" }}>
          <Loader2 size={20} className="dkt-spin" /> {t("Cargando…", "Loading…")}
        </div>
      </div>
    );
  }

  // Already enrolled (either on load or after a duplicate submit).
  if (data.alreadyDone || (done && done.already)) {
    return (
      <div style={page}>
        <div style={{ ...card, ...pad, textAlign: "center" }}>
          <CheckCircle2 size={40} style={{ color: "#16a34a", marginBottom: 8 }} />
          <h2 style={{ margin: "6px 0" }}>{t("Tu inscripción ya está completa", "Your enrollment is already complete")}</h2>
          <p style={{ color: "#64748b", fontSize: 14 }}>
            {t("Ya recibimos tus datos. Si necesitas cambiar algo, contacta a tu agente.",
               "We already have your details. If you need to change anything, contact your agent.")}
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={page}>
        <div style={{ ...card, ...pad, textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <Check size={34} style={{ color: "#16a34a" }} />
          </div>
          <h2 style={{ margin: "4px 0" }}>{t("¡Inscripción enviada! 🎉", "Enrollment sent! 🎉")}</h2>
          <p style={{ color: "#475569", fontSize: 14, marginTop: 10 }}>
            {t(`Gracias. ${done.agentName || "Tu agente"} ya tiene tu información y continuará con tu inscripción.`,
               `Thank you. ${done.agentName || "Your agent"} now has your information and will continue your enrollment.`)}
          </p>
          <p style={{ color: "#64748b", fontSize: 13, marginTop: 12 }}>
            {t("Te enviamos un correo de confirmación.", "We emailed you a confirmation.")}
          </p>
        </div>
      </div>
    );
  }

  const plan = data.plan;
  const agencyName = data.agencyName || data.agent?.company || "";

  return (
    <div style={page}>
      <div style={card}>
        {/* Header */}
        <div style={{ ...pad, background: "linear-gradient(135deg,#0a558c,#0891b2)", color: "#fff" }}>
          <div style={{ fontWeight: 800, fontSize: 19 }}>{t("Completa tu inscripción", "Complete your enrollment")}</div>
          {agencyName && <div style={{ fontSize: 13, opacity: .9, marginTop: 2 }}>{agencyName}</div>}
          {plan && (
            <div style={{ marginTop: 12, background: "rgba(255,255,255,.15)", borderRadius: 10, padding: "10px 14px", fontSize: 13.5 }}>
              ✅ {t("Plan elegido", "Chosen plan")}: <strong>{[plan.carrier, plan.description].filter(Boolean).join(" · ")}</strong>
              {plan.price ? ` · $${Number(plan.price).toFixed(2)}/mo` : ""}
            </div>
          )}
        </div>

        <div style={pad}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "9px 12px", marginBottom: 16, fontSize: 12.5, color: "#0a558c" }}>
            <ShieldCheck size={16} /> {t("Tus datos viajan y se guardan cifrados.", "Your information is transmitted and stored encrypted.")}
          </div>

          {/* Contact */}
          <Row>
            <Field label={t("Nombre", "First name")}><input style={inputSt} value={form.firstName} onChange={set("firstName")} /></Field>
            <Field label={t("Apellido", "Last name")}><input style={inputSt} value={form.lastName} onChange={set("lastName")} /></Field>
          </Row>
          <Field label={t("Correo electrónico", "Email")}><input type="email" style={inputSt} value={form.email} onChange={set("email")} /></Field>
          <Row>
            <Field label={t("Teléfono", "Phone")}><input type="tel" style={inputSt} value={form.phone} onChange={set("phone")} /></Field>
            <Field label={t("Fecha de nacimiento", "Date of birth")}><input type="date" style={inputSt} value={form.dateOfBirth} onChange={set("dateOfBirth")} /></Field>
          </Row>

          {/* Address */}
          <Field label={t("Dirección", "Address")}><input style={inputSt} value={form.address} onChange={set("address")} /></Field>
          <Row>
            <Field label={t("Ciudad", "City")}><input style={inputSt} value={form.city} onChange={set("city")} /></Field>
            <Field label={t("Estado", "State")}><input style={inputSt} value={form.state} onChange={set("state")} maxLength={20} /></Field>
            <Field label={t("Código postal", "ZIP")}><input style={inputSt} value={form.zipCode} onChange={set("zipCode")} maxLength={12} /></Field>
          </Row>

          {/* Sensitive / optional */}
          <Row>
            <Field label={`${t("Seguro Social (SSN/ITIN)", "Social Security (SSN/ITIN)")} · ${t("opcional", "optional")}`}>
              <input style={inputSt} value={form.ssn} onChange={set("ssn")} placeholder="•••-••-••••" inputMode="numeric" />
            </Field>
            <Field label={`${t("Estado civil", "Marital status")} · ${t("opcional", "optional")}`}>
              <select style={inputSt} value={form.maritalStatus} onChange={set("maritalStatus")}>
                {MARITAL.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
              </select>
            </Field>
          </Row>
          <Field label={`${t("Condiciones de salud / notas para tu agente", "Health conditions / notes for your agent")} · ${t("opcional", "optional")}`}>
            <textarea rows={3} style={{ ...inputSt, resize: "vertical", fontFamily: "inherit" }} value={form.healthNotes} onChange={set("healthNotes")} />
          </Field>

          {/* Consent */}
          <label style={{ display: "flex", gap: 9, alignItems: "flex-start", margin: "8px 0 16px", cursor: "pointer", fontSize: 13, color: "#475569" }}>
            <input type="checkbox" checked={form.consent} onChange={set("consent")} style={{ marginTop: 3, width: 16, height: 16, flex: "0 0 auto" }} />
            <span>{t(`Autorizo a ${agencyName || "mi agente"} a usar esta información para procesar mi inscripción al seguro.`,
                     `I authorize ${agencyName || "my agent"} to use this information to process my insurance enrollment.`)}</span>
          </label>

          {submitError && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 9, padding: "10px 12px", fontSize: 13.5, marginBottom: 12 }}>{submitError}</div>
          )}

          <button type="button" onClick={handleSubmit} disabled={submitting}
            style={{ width: "100%", padding: "13px", borderRadius: 11, border: "none", background: "linear-gradient(135deg,#0a558c,#0891b2)", color: "#fff", fontSize: 15.5, fontWeight: 800, cursor: submitting ? "wait" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {submitting ? <Loader2 size={18} className="dkt-spin" /> : <Check size={18} />}
            {t("Enviar mi inscripción", "Submit my enrollment")}
          </button>
        </div>

        <div style={{ textAlign: "center", padding: "12px", fontSize: 11.5, color: "#94a3b8" }}>
          {t("Protegido con", "Secured with")} <strong style={{ color: "#0891b2" }}>Deskte</strong>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12, flex: 1, minWidth: 0 }}>
      <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#475569", marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>{children}</div>;
}
